import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Mail, Send, CircleCheck, CircleAlert } from "lucide-react";
import { toast } from "sonner";

/**
 * EmailHealthCard — tells the owner whether THIS deployment can actually
 * deliver email (password resets, notifications). Surfaces the silent
 * "RESEND_API_KEY missing in production" failure mode and offers a
 * one-click test send to the admin's own inbox.
 */
const EmailHealthCard = () => {
  const [data, setData] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/email-health");
      setData(r.data);
    } catch {
      setData(null);
    }
  };
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/email-health/test");
      toast.success(`Test email sent to ${r.data.to} — check your inbox (and spam folder)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
      load();
    } finally {
      setTesting(false);
    }
  };

  if (!data) return null;
  const bad = !data.configured;

  return (
    <div className="obs-card" data-testid="email-health-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Mail className={`h-5 w-5 ${bad ? "text-red-400" : "text-blaze"}`} />
          <div>
            <div className="overline">Email health</div>
            <h3 className="font-heading text-2xl uppercase flex items-center gap-2">
              {bad ? (
                <>
                  <CircleAlert className="h-5 w-5 text-red-400" /> Not configured
                </>
              ) : (
                <>
                  <CircleCheck className="h-5 w-5 text-emerald-400" /> Sending enabled
                </>
              )}
            </h3>
          </div>
        </div>
        <button
          type="button"
          data-testid="email-health-test-btn"
          onClick={sendTest}
          disabled={testing}
          className="inline-flex items-center gap-2 border border-obsidian-600 px-3 py-2 rounded-sm text-xs uppercase tracking-wider text-zinc-300 hover:border-blaze hover:text-blaze disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" /> {testing ? "Sending…" : "Send me a test email"}
        </button>
      </div>

      {bad ? (
        <p className="text-sm text-red-400 mt-3" data-testid="email-health-warning">
          RESEND_API_KEY is missing on this deployment — password resets, session alerts, and all
          other emails are silently NOT being sent. Add the key to this deployment's environment
          variables and redeploy.
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-400">
          <span>From: <span className="text-zinc-200">{data.email_from}</span></span>
          <span>Sent (24h): <span className="text-emerald-400 font-data">{data.sent_24h}</span></span>
          <span>Failed (24h): <span className={`font-data ${data.failed_24h ? "text-red-400" : "text-zinc-200"}`}>{data.failed_24h}</span></span>
        </div>
      )}

      {(data.recent || []).length > 0 && (
        <details className="mt-3">
          <summary className="text-[10px] text-zinc-500 uppercase tracking-widest cursor-pointer hover:text-zinc-300">
            Recent sends ({data.recent.length})
          </summary>
          <div className="mt-2 space-y-1">
            {data.recent.map((e, i) => (
              <div key={i} className="text-[11px] flex items-center gap-2 text-zinc-400">
                <span className={e.ok ? "text-emerald-400" : "text-red-400"}>{e.ok ? "✓" : "✗"}</span>
                <span className="w-32 truncate">{e.to}</span>
                <span className="flex-1 truncate">{e.subject}</span>
                <span className="text-zinc-600 shrink-0">{(e.at || "").slice(5, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default EmailHealthCard;
